import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatMoney } from "../../lib/api";
import { Package, Users, ShoppingBag, Tag, TrendingUp, ClipboardList, UserCheck, PackageOpen, CheckCircle2, ChevronRight, BarChart3 } from "lucide-react";
import {
  ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
} from "recharts";

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [orders, setOrders] = useState([]);
  const [timeseries, setTimeseries] = useState(null);
  const [tsRange, setTsRange] = useState(14); // 7, 14, 30

  useEffect(() => {
    Promise.all([api.get("/admin/stats"), api.get("/admin/orders")]).then(([s, o]) => {
      setStats(s.data);
      setOrders(o.data.slice(0, 8));
    });
  }, []);

  useEffect(() => {
    api.get("/admin/stats/timeseries", { params: { days: tsRange } })
      .then((r) => setTimeseries(r.data))
      .catch(() => setTimeseries({ series: [] }));
  }, [tsRange]);

  const cards = [
    { label: "Aktív cikkszámok", value: stats?.total_products?.toLocaleString("hu-HU") ?? "—", icon: Package, id: "skus" },
    { label: "Műhelyek", value: stats?.total_customers ?? "—", icon: Users, id: "workshops" },
    { label: "Rendelések", value: stats?.total_orders ?? "—", icon: ShoppingBag, id: "orders" },
    { label: "Árszintek", value: stats?.total_price_groups ?? "—", icon: Tag, id: "tiers" },
  ];

  return (
    <div>
      <h1 className="text-2xl font-bold mb-2 uppercase tracking-wide">Áttekintés</h1>
      <div className="border-b-2 mb-5" style={{ borderColor: "var(--brand)" }} />

      {/* Mai teendők */}
      <div className="card-elev mb-5 overflow-hidden" data-testid="todo-bar">
        <div className="section-title flex items-center gap-2" style={{ background: "linear-gradient(180deg, #0f766e 0%, #134e4a 100%)" }}>
          <ClipboardList size={14} /> Mai teendők
        </div>
        <div className="bg-white p-3">
          {stats && stats.todo_pending_orders === 0 && stats.todo_partial_orders === 0 && stats.todo_pending_approvals === 0 ? (
            <div className="flex items-center gap-2 text-sm py-2 px-1" style={{ color: "#0A8043" }} data-testid="todo-all-done">
              <CheckCircle2 size={16} /> Minden rendben — nincs feldolgozásra váró rendelés és jóváhagyásra váró vevő. ☕
            </div>
          ) : (
            <div className="grid sm:grid-cols-3 gap-3">
              <TodoItem
                to="/admin/orders"
                icon={ShoppingBag}
                count={stats?.todo_pending_orders}
                label="rendelés vár feldolgozásra"
                color="#C58800"
                bg="#FFF4E0"
                testid="todo-pending-orders"
              />
              <TodoItem
                to="/admin/orders"
                icon={PackageOpen}
                count={stats?.todo_partial_orders}
                label="rendelés részben megjött"
                color="#2680C2"
                bg="#E8F0FA"
                testid="todo-partial-orders"
              />
              <TodoItem
                to="/admin/customers"
                icon={UserCheck}
                count={stats?.todo_pending_approvals}
                label="vevő vár jóváhagyásra"
                color="#c8102e"
                bg="#FDECEC"
                testid="todo-pending-approvals"
              />
            </div>
          )}
        </div>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-5" data-testid="admin-stat-cards">
        {cards.map((c) => (
          <div key={c.label} className="card-elev p-4" data-testid={`stat-${c.id}`}>
            <div className="flex items-center justify-between mb-3">
              <div
                className="w-9 h-9 rounded flex items-center justify-center"
                style={{ background: "var(--surface-elev)", border: "1px solid var(--border)" }}
              >
                <c.icon size={16} style={{ color: "var(--brand)" }} />
              </div>
              <div className="text-[11px] uppercase font-bold" style={{ color: "var(--text-mute)" }}>{c.label}</div>
            </div>
            <div className="text-2xl font-bold" style={{ color: "var(--text)" }}>{c.value}</div>
          </div>
        ))}
      </div>

      <div className="card-elev mb-6 overflow-hidden">
        <div className="section-title flex-wrap justify-between">
          <span>Bruttó rendelési érték (összesen)</span>
          <span className="flex items-center gap-2 text-[10px]">
            <span className="w-2 h-2 rounded-full" style={{ background: "#A6FFB8" }} />
            ÉLŐ ADAT
          </span>
        </div>
        <div className="p-6 flex items-center gap-3" style={{ background: "linear-gradient(180deg, #fff, #F5F5F5)" }}>
          <TrendingUp size={28} style={{ color: "var(--brand)" }} />
          <div className="text-4xl font-bold" data-testid="stat-revenue" style={{ color: "var(--brand)" }}>
            {stats ? formatMoney(stats.total_revenue) : "—"}
          </div>
        </div>
      </div>

      {/* Daily orders & revenue chart */}
      <div className="card-elev mb-6 overflow-hidden" data-testid="stats-chart-card">
        <div className="section-title flex-wrap justify-between gap-2">
          <span className="flex items-center gap-2"><BarChart3 size={14} /> Napi rendelések és bevétel</span>
          <div className="flex items-center gap-1" data-testid="chart-range-toggle">
            {[7, 14, 30].map((d) => (
              <button
                key={d}
                onClick={() => setTsRange(d)}
                className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded transition-colors"
                style={{
                  background: tsRange === d ? "#fff" : "transparent",
                  color: tsRange === d ? "var(--brand)" : "#fff",
                  border: "1px solid rgba(255,255,255,0.4)",
                }}
                data-testid={`chart-range-${d}`}
              >
                {d} nap
              </button>
            ))}
          </div>
        </div>
        <div className="p-3 bg-white" style={{ minHeight: 280 }}>
          {!timeseries ? (
            <div className="text-sm text-center py-12" style={{ color: "var(--text-mute)" }}>Betöltés...</div>
          ) : timeseries.series.every((p) => p.orders === 0) ? (
            <div className="text-sm text-center py-12" style={{ color: "var(--text-mute)" }}>
              Nincs rendelés az elmúlt {tsRange} napban.
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <ComposedChart
                data={timeseries.series.map((p) => ({
                  ...p,
                  day: new Date(p.date).toLocaleDateString("hu-HU", { month: "short", day: "2-digit" }),
                }))}
                margin={{ top: 10, right: 15, left: 0, bottom: 0 }}
              >
                <CartesianGrid stroke="#eee" strokeDasharray="3 3" />
                <XAxis dataKey="day" tick={{ fontSize: 11 }} stroke="#666" />
                <YAxis yAxisId="left" tick={{ fontSize: 11 }} stroke="#2680C2" allowDecimals={false} />
                <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} stroke="#0f766e" />
                <Tooltip
                  formatter={(v, name) => name === "Bevétel (€)" ? [formatMoney(v), name] : [v, name]}
                  labelStyle={{ fontWeight: 700, fontSize: 12 }}
                  contentStyle={{ fontSize: 12, border: "1px solid #ddd", borderRadius: 4 }}
                />
                <Legend wrapperStyle={{ fontSize: 11 }} iconType="circle" />
                <Bar yAxisId="left" dataKey="orders" name="Rendelések" fill="#2680C2" radius={[3, 3, 0, 0]} barSize={18} />
                <Line yAxisId="right" type="monotone" dataKey="revenue" name="Bevétel (€)" stroke="#0f766e" strokeWidth={2.5} dot={{ r: 3 }} activeDot={{ r: 5 }} />
              </ComposedChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      <section className="card-elev overflow-hidden">
        <div className="section-title">Legutóbbi rendelések</div>
        <div className="overflow-x-auto">
          <table className="table-b2b" data-testid="admin-orders-table">
            <thead>
              <tr>
                <th>Rendelés</th>
                <th>Vevő</th>
                <th>Műhely</th>
                <th>Tételek</th>
                <th className="text-right">Összesen</th>
                <th>Leadva</th>
              </tr>
            </thead>
            <tbody>
              {orders.length === 0 ? (
                <tr><td colSpan={6} className="text-center py-8" style={{ color: "var(--text-mute)" }}>Még nincs rendelés.</td></tr>
              ) : (
                orders.map((o) => (
                  <tr key={o.id}>
                    <td><span className="font-mono font-bold" style={{ color: "var(--brand)" }}>#{o.id.slice(0, 8).toUpperCase()}</span></td>
                    <td className="text-sm">{o.user_email}</td>
                    <td className="font-bold">{o.user_company || "—"}</td>
                    <td className="font-mono">{o.items.length}</td>
                    <td className="text-right font-mono font-bold">{formatMoney(o.total)}</td>
                    <td className="text-sm" style={{ color: "var(--text-mute)" }}>{new Date(o.created_at).toLocaleDateString("hu-HU")}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

function TodoItem({ to, icon: Icon, count, label, color, bg, testid }) {
  const n = count ?? 0;
  const muted = n === 0;
  return (
    <Link
      to={to}
      className="flex items-center justify-between gap-2 p-3 rounded border transition-colors hover:shadow-sm"
      style={muted
        ? { background: "#FAFAFA", borderColor: "var(--border)", color: "var(--text-mute)", opacity: 0.7 }
        : { background: bg, borderColor: color, color: "var(--text)" }}
      data-testid={testid}
    >
      <div className="flex items-center gap-3 min-w-0">
        <Icon size={18} style={{ color: muted ? "var(--text-disabled)" : color }} />
        <div className="text-sm">
          <span className="text-xl font-bold font-mono mr-1.5" style={{ color: muted ? "var(--text-mute)" : color }}>
            {count ?? "—"}
          </span>
          {label}
        </div>
      </div>
      <ChevronRight size={14} style={{ color: muted ? "var(--text-disabled)" : color }} />
    </Link>
  );
}
